import React from 'react';
import { format } from 'date-fns';
import {
  type Weekday,
  type ScheduleTimeSlot,
} from '@/features/plants/schemas/schedule-plant.schema';
import { SCHEDULE_TIME_SLOT_WINDOW_VERTICAL_INSET_PX } from '@/features/plants/components/plant-schedule/schedule/constants.ts';
import { cn } from '@/utils/utils';
import {
  getPowerSetpointType,
  getTimeSlotStatus,
  POWER_SETPOINT_COLORS,
  TIME_SLOT_STATUS_STYLES,
} from './schedule-time-slot-colors.ts';

const HATCH_OVERLAY_STYLE: React.CSSProperties = {
  backgroundImage:
    'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(0,0,0,0.18) 5px, rgba(0,0,0,0.18) 7px)',
};

function formatValidUntil(validUntil?: string | null): string | null {
  if (!validUntil) return null;
  const date = new Date(validUntil);
  if (Number.isNaN(date.getTime())) return null;
  return format(date, 'MMM d, yyyy HH:mm');
}

export interface ScheduleTimeSlotWindow {
  slot: ScheduleTimeSlot;
  day: Weekday;
  top: number;
  left: number;
  width: number;
  height: number;
}

export function ScheduleTimeSlotWindow({
  timeSlot,
  onTimeSlotClick,
  onStartMove,
  onStartResizeLeft,
  onStartResizeRight,
  dayColumnWidthPx = 132,
  suppressClicks = false,
}: {
  timeSlot: ScheduleTimeSlotWindow;
  onTimeSlotClick: (day: Weekday, slot: ScheduleTimeSlot, e: React.MouseEvent) => void;
  onStartMove: (timeSlot: ScheduleTimeSlotWindow, e: React.MouseEvent) => void;
  onStartResizeLeft: (timeSlot: ScheduleTimeSlotWindow, e: React.MouseEvent) => void;
  onStartResizeRight: (timeSlot: ScheduleTimeSlotWindow, e: React.MouseEvent) => void;
  dayColumnWidthPx?: number;
  suppressClicks?: boolean;
}) {
  const powerType = getPowerSetpointType(timeSlot.slot.powerSetpoint);
  const colors = POWER_SETPOINT_COLORS[powerType];
  const status = getTimeSlotStatus(timeSlot.slot);
  const statusStyle = TIME_SLOT_STATUS_STYLES[status];
  const validUntilLabel = formatValidUntil(timeSlot.slot.validUntil);
  // Flip card below the slot when it's in the first row to avoid ScrollArea clipping
  const cardBelow = timeSlot.top < 100;

  return (
    <div
      className="group absolute z-10 cursor-grab hover:z-50"
      style={{
        top: `${timeSlot.top + SCHEDULE_TIME_SLOT_WINDOW_VERTICAL_INSET_PX}px`,
        left: `${dayColumnWidthPx + timeSlot.left}px`,
        width: `${timeSlot.width}px`,
        height: `${Math.max(
          0,
          timeSlot.height - SCHEDULE_TIME_SLOT_WINDOW_VERTICAL_INSET_PX * 2
        )}px`,
      }}
      onMouseDown={(e) => onStartMove(timeSlot, e)}
      onClick={(e) => {
        e.stopPropagation();
        if (suppressClicks) return;
        onTimeSlotClick(timeSlot.day, timeSlot.slot, e);
      }}
    >
      <div
        className={cn(
          'absolute inset-0 flex flex-col items-start justify-start rounded border-2 py-1 shadow-sm transition-colors',
          statusStyle.container,
          colors.border,
          colors.bg,
          colors.bgHover
        )}
      >
        {statusStyle.hatch && (
          <div
            className="pointer-events-none absolute inset-0 rounded"
            style={HATCH_OVERLAY_STYLE}
          />
        )}

        <div
          className="absolute top-0 left-0 h-full w-2 cursor-ew-resize rounded-l"
          onMouseDown={(e) => {
            e.stopPropagation();
            onStartResizeLeft(timeSlot, e);
          }}
        />
        <div
          className="absolute top-0 right-0 h-full w-2 cursor-ew-resize rounded-r"
          onMouseDown={(e) => {
            e.stopPropagation();
            onStartResizeRight(timeSlot, e);
          }}
        />

        <div className="pointer-events-none relative z-10 flex h-full w-full flex-col items-center justify-center">
          <span
            className={cn(
              'bg-opacity-90 rounded bg-white px-2 py-1 text-xs font-semibold shadow-sm',
              colors.text
            )}
          >
            {timeSlot.slot.powerSetpoint}kW
          </span>

          <div
            className={cn(
              'flex h-0.5 translate-y-1 items-center justify-center',
              timeSlot.width > 64 ? 'w-full' : 'w-fit'
            )}
          >
            <div className="bottom-0 hidden w-full justify-between gap-2 px-1 text-[10px] leading-tight font-medium text-black/80 group-hover:flex">
              <span>{timeSlot.slot.startTime}</span>
              <span>{timeSlot.slot.endTime}</span>
            </div>
          </div>
        </div>
      </div>

      <div
        className={cn(
          'pointer-events-none absolute left-1/2 z-50 hidden -translate-x-1/2 whitespace-nowrap group-hover:block',
          cardBelow ? 'top-full mt-1.5' : 'bottom-full mb-1.5'
        )}
      >
        <div className="rounded-md border bg-white px-3 py-2 shadow-md">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1.5">
              <div className={cn('h-2.5 w-2.5 rounded-sm', colors.dot)} />
              <span className="text-sm font-medium text-slate-800">{colors.label}</span>
            </div>
            <span className="text-xs text-slate-500 tabular-nums">
              {timeSlot.slot.startTime} {'\u2013'} {timeSlot.slot.endTime}
            </span>
            <span className={cn('text-sm font-semibold tabular-nums', colors.text)}>
              {timeSlot.slot.powerSetpoint > 0 ? '+' : ''}
              {timeSlot.slot.powerSetpoint} kW
            </span>
            {statusStyle.label && (
              <span
                className={cn(
                  'mt-0.5 w-fit rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase',
                  status === 'expired'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-slate-200 text-slate-600'
                )}
              >
                {statusStyle.label}
              </span>
            )}
            {validUntilLabel && (
              <span className="text-xs text-slate-500 tabular-nums">
                {status === 'expired' ? 'Expired' : 'Active until'} {validUntilLabel}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
